/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {calculateErrorMessage} from 'common/helpers'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {ChatbotMode} from '../config/chatbotModeConfig'
import {fetchRecommendedVoices} from '../voice/helpers/voiceService'
import {generateTranscriptId} from '../voice/helpers/voiceUtils'
import {useVoiceCallApi} from './useVoiceCallApi'
import {useVoiceCallAudio} from './useVoiceCallAudio'
import {useVoiceCallStateRegistry} from './useVoiceCallStateRegistry'
import {useVoiceCallVAD} from './useVoiceCallVAD'

export type VoiceCallStatus =
  | 'idle'
  | 'choosing_transcription'
  | 'choosing_voice'
  | 'connecting'
  | 'listening'
  | 'responding'
  | 'muted'
  | 'walkthrough_paused'
  | 'ended'
  | 'error'
  | 'interrupted'

export interface VoiceCallError {
  type:
    | 'permission_denied'
    | 'permission_revoked'
    | 'speech_recognition_not_supported'
    | 'microphone_not_accessible'
    | 'speech_recognition_error'
    | 'unknown'
  message: string
  retryable: boolean
}

export interface TranscriptLine {
  id: string
  speaker: 'user' | 'ai'
  text: string
  timestamp: number
  interim?: boolean
  /** A user line the in-browser recogniser produced while listening. It is shown immediately so the
   *  panel is not empty, then replaced by the server's transcription of the same audio once the
   *  response comes back — see `reconcileUserTranscript`. */
  provisional?: boolean
  isProgressive?: boolean
  progressiveText?: string
  totalText?: string
  isComplete?: boolean
}

export interface VoiceCallStateOptions {
  getConversationHistory?: () => Array<{query: string; answer: string}>
  onAppendConversationPair?: (pair: {query: string; answer: string}) => void
  onInsertChatEvent?: (event: any) => void
  transcriptionEnabled?: boolean
  selectedVoice?: string | null
  isMuted?: boolean
  onWalkthroughResume?: () => void
  chatbotMode?: ChatbotMode
  getMenuSelectedPath?: () => string
}

export const useVoiceCallState = (options: VoiceCallStateOptions = {}) => {
  const {
    getConversationHistory,
    transcriptionEnabled: initialTranscriptionEnabled = null,
    selectedVoice: initialSelectedVoice = null,
    isMuted: initialIsMuted = false,
    onWalkthroughResume,
    chatbotMode = 'general',
    getMenuSelectedPath,
  } = options

  const {state, refs, userTranscriptExists} = useVoiceCallStateRegistry()
  const {
    status,
    setStatus,
    error,
    setError,
    transcript,
    setTranscript,
    interimUserTranscript,
    setInterimUserTranscript,
    micLevel,
    setMicLevel,
    awaitingResponse,
    setAwaitingResponse,
    isAiSpeaking,
    setIsAiSpeaking,
  } = state

  const [transcriptionEnabled, setTranscriptionEnabled] = useState<boolean | null>(
    initialTranscriptionEnabled
  )
  const [selectedVoice, setSelectedVoice] = useState<string | null>(initialSelectedVoice)
  const [isMuted, setIsMuted] = useState(initialIsMuted)
  const [voices, setVoices] = useState<Record<
    string,
    Array<{name: string; gender?: string; description?: string}>
  > | null>(null)
  const [transcriptionStage, setTranscriptionStage] = useState<
    'idle' | 'filler_transcribing' | 'answer_transcribing'
  >('idle')

  useEffect(() => {
    refs.transcriptionEnabledRef.current = transcriptionEnabled
  }, [transcriptionEnabled])
  useEffect(() => {
    refs.mutedRef.current = isMuted
  }, [isMuted])
  useEffect(() => {
    refs.statusRef.current = status
  }, [status])
  useEffect(() => {
    refs.awaitingResponseRef.current = awaitingResponse
  }, [awaitingResponse])
  useEffect(() => {
    refs.isAiSpeakingRef.current = isAiSpeaking
  }, [isAiSpeaking])
  useEffect(() => {
    refs.interimUserTranscriptRef.current = interimUserTranscript
  }, [interimUserTranscript])

  const commitTranscript = useCallback(
    (text: string, speaker: 'user' | 'ai', opts?: {provisional?: boolean}) => {
      if (!text.trim()) return
      if (speaker === 'user' && userTranscriptExists(text)) return
      const newLine: TranscriptLine = {
        id: generateTranscriptId(speaker),
        speaker,
        text: text.trim(),
        timestamp: Date.now(),
        ...(opts?.provisional ? {provisional: true} : {}),
      }
      setTranscript((prev) => [...prev, newLine])
      if (speaker === 'user') refs.lastCommittedUserTranscriptRef.current = text.trim()
    },
    [setTranscript, userTranscriptExists]
  )

  /**
   * Replaces the browser recogniser's guess for the utterance just sent with the server's
   * transcription of the same audio.
   *
   * The server transcribes the whole recording after the fact, so it beats the in-browser engine —
   * which decodes in real time, restarts between segments, and until now was the only thing the
   * panel ever displayed even though the better text was already on the response.
   *
   * @param text - Transcription from the `X-Transcription-Text` response header.
   */
  const reconcileUserTranscript = useCallback(
    (text: string) => {
      const authoritative = text.trim()
      if (!authoritative) return

      setTranscript((prev) => {
        // Everything provisional at the tail belongs to the utterance we just sent: the recogniser
        // commits one line per segment, so a long sentence can leave several behind.
        let cut = prev.length
        while (cut > 0 && prev[cut - 1].speaker === 'user' && prev[cut - 1].provisional) cut -= 1

        const kept = prev.slice(0, cut)
        const last = kept[kept.length - 1]
        // Nothing provisional to replace and the line is already there (a repeat header, or the
        // recogniser happened to agree) — leave the list untouched.
        if (cut === prev.length && last?.speaker === 'user' && last.text === authoritative) {
          return prev
        }

        return [
          ...kept,
          {
            id: generateTranscriptId('user'),
            speaker: 'user' as const,
            text: authoritative,
            timestamp: Date.now(),
          },
        ]
      })

      refs.lastCommittedUserTranscriptRef.current = authoritative
    },
    [setTranscript, refs]
  )

  const bridgeRef = useRef<any>({audio: null, api: null, vad: null})

  const actionsForAudio = useMemo(
    () => ({setStatus, setIsAiSpeaking, setAwaitingResponse, setTranscript}),
    [setStatus, setIsAiSpeaking, setAwaitingResponse, setTranscript]
  )
  const audio = useVoiceCallAudio(refs, actionsForAudio)

  const actionsForApi = useMemo(
    () => ({
      setStatus,
      setIsAiSpeaking,
      setAwaitingResponse,
      setTranscript,
      setInterimUserTranscript,
      setTranscriptionStage,
      reconcileUserTranscript,
    }),
    [
      setStatus,
      setIsAiSpeaking,
      setAwaitingResponse,
      setTranscript,
      setInterimUserTranscript,
      setTranscriptionStage,
      reconcileUserTranscript,
    ]
  )
  const audioActionsBridge = useMemo(
    () => ({
      stopUtteranceRecording: () => bridgeRef.current.audio?.stopUtteranceRecording(),
      playFillerAudio: (b: Blob) => bridgeRef.current.audio?.playFillerAudio(b),
      playFullResponseAudio: (b: Blob, u: any) =>
        bridgeRef.current.audio?.playFullResponseAudio(b, u),
      startListening: () => bridgeRef.current.vad?.startListening(),
      startUtteranceRecording: () => bridgeRef.current.audio?.startUtteranceRecording(),
      startProgressiveTranscript: (t: string) =>
        bridgeRef.current.audio?.startProgressiveTranscript(t),
      stopProgressiveTranscript: () => bridgeRef.current.audio?.stopProgressiveTranscript(),
    }),
    []
  )
  const apiOptions = useMemo(
    () => ({getConversationHistory, selectedVoice, chatbotMode, getMenuSelectedPath}),
    [getConversationHistory, selectedVoice, chatbotMode, getMenuSelectedPath]
  )
  const api = useVoiceCallApi(refs, actionsForApi, audioActionsBridge, apiOptions)

  const handleUserInterruptAndRestart = useCallback(
    (t?: string) => {
      refs.abortRef.current?.abort()
      const {audio, api, vad} = bridgeRef.current
      if (audio) {
        audio.stopAudioPlayback()
        audio.stopFillerAudio()
        audio.stopProgressiveTranscript()
      }
      refs.inFlightRef.current = false
      refs.responseRequestedRef.current = false
      setIsAiSpeaking(false)
      setAwaitingResponse(false)
      if (t?.trim()) commitTranscript(t.trim(), 'user', {provisional: true})
      api?.unblockListening('interrupted', true)
      setStatus('listening')
      const tId = window.setTimeout(() => {
        try {
          if (isPausedRef.current) {
            return
          }
          vad?.startListening()
          audio?.startUtteranceRecording()
        } finally {
          refs.timeoutIdsRef.current.delete(tId as any)
        }
      }, 200)
      refs.timeoutIdsRef.current.add(tId as any)
    },
    [commitTranscript, setStatus, setIsAiSpeaking, setAwaitingResponse, refs]
  )

  const actionsForVAD = useMemo(
    () => ({setMicLevel, setError, setInterimUserTranscript}),
    [setMicLevel, setError, setInterimUserTranscript]
  )
  const callbacksForVAD = useMemo(
    () => ({
      handleUserInterruptAndRestart,
      userTranscriptExists,
      commitTranscript,
      shouldProcessListening: () =>
        !refs.listeningBlockedRef.current &&
        !refs.isAiSpeakingRef.current &&
        !refs.inFlightRef.current &&
        !refs.responseRequestedRef.current &&
        !refs.walkthroughActiveRef.current &&
        refs.statusRef.current === 'listening' &&
        !refs.mutedRef.current,
    }),
    [handleUserInterruptAndRestart, userTranscriptExists, commitTranscript, refs]
  )
  const VAD = useVoiceCallVAD(refs, actionsForVAD, callbacksForVAD)

  const apiRef = useRef(api)
  const audioRef = useRef(audio)

  const [isPaused, setIsPaused] = useState(false)
  const isPausedRef = useRef(false)
  const prevStatusRef = useRef<VoiceCallStatus | null>(null)
  const prevAiSpeakingRef = useRef<boolean>(false)
  const pausedTimeoutsRef = useRef<Set<any>>(new Set())

  const chatWasOpenBeforeWalkthroughRef = useRef<boolean>(false)
  const overlayPrestartSeenRef = useRef<number | null>(null)
  const overlayStartSeenRef = useRef<number | null>(null)
  const pendingResumeRef = useRef<boolean>(false)
  const pendingResumeTimerRef = useRef<number | null>(null)
  const lastWalkthroughEventTsRef = useRef<number>(0)

  type WalkthroughSnapshot = {
    transcript: TranscriptLine[]
    interimUserTranscript: string
    selectedVoice: string | null
    isMuted: boolean
    lastCommittedUserTranscript: string | null
    awaitingResponse: boolean
    isAiSpeaking: boolean
    status: VoiceCallStatus | null
    transcriptionEnabled?: boolean | null
    playback?: {
      type: 'full' | 'filler' | 'unknown' | 'none'
      blob?: Blob | null
      url?: string | null
      position?: number
      transcriptId?: string | null
      text?: string | null
      queuedAnswerBlob?: Blob | null
      queuedAnswerUrl?: string | null
    }
  }
  const walkthroughSnapshotRef = useRef<WalkthroughSnapshot | null>(null)

  const createOverlay = () => {
    try {
      if (typeof document === 'undefined') return
      if (document.getElementById('voice-walkthrough-overlay')) return
      const el = document.createElement('div')
      el.id = 'voice-walkthrough-overlay'
      el.style.position = 'fixed'
      el.style.left = '0'
      el.style.top = '0'
      el.style.right = '0'
      el.style.bottom = '0'
      el.style.zIndex = '99999'
      el.style.display = 'flex'
      el.style.alignItems = 'center'
      el.style.justifyContent = 'center'
      el.style.background = 'rgba(0,0,0,0.6)'
      el.style.color = '#fff'
      el.style.flexDirection = 'column'
      el.style.pointerEvents = 'auto'
      el.innerHTML = `<div style="text-align:center;max-width:360px;padding:24px;border-radius:8px;background:rgba(0,0,0,0.6);">
        <h3 style=\\"margin:0 0 8px 0\\">Walkthrough starting…</h3>
        <div>Voice input paused</div>
      </div>`
      document.body.appendChild(el)
    } catch (e) {
      calculateErrorMessage(e)
    }
  }

  const removeOverlay = () => {
    try {
      if (typeof document === 'undefined') return
      const el = document.getElementById('voice-walkthrough-overlay')
      if (el) {
        el.remove()
      }
    } catch (e) {
      calculateErrorMessage(e)
    }
  }

  const restoreChatIfClosed = async () => {
    try {
      if (typeof document === 'undefined') return
      const drawer = document.getElementById('kt_drawer_chat')
      const isOpen = !!(
        drawer &&
        (drawer.classList.contains('show') ||
          drawer.getAttribute('data-kt-drawer-show') === 'true' ||
          (drawer.getBoundingClientRect && drawer.getBoundingClientRect().width > 0))
      )
      if (isOpen) return

      const openBtn = document.querySelector(
        'button[aria-label="Open chatbot portal"]'
      ) as HTMLElement | null
      const toggle = document.getElementById('kt_drawer_chat_toggle') as HTMLElement | null
      const minimizeBtn = document.querySelector(
        'button[aria-label="Minimize chatbot portal"]'
      ) as HTMLElement | null

      if (openBtn && typeof (openBtn as any).click === 'function') {
        ;(openBtn as any).click()
      } else if (toggle && typeof (toggle as any).click === 'function') {
        ;(toggle as any).click()
      } else if (minimizeBtn && typeof (minimizeBtn as any).click === 'function') {
        ;(minimizeBtn as any).click()
      } else if (drawer) {
        drawer.classList.add('show')
      }

      await new Promise((r) => setTimeout(r, 120))
    } catch (e) {
      calculateErrorMessage(e)
    }
  }

  useEffect(() => {
    bridgeRef.current = {audio, api, vad: VAD}
    apiRef.current = api
    audioRef.current = audio
  })

  const startCallFlow = useCallback(() => {
    setStatus('choosing_transcription')
    setError(null)
    try {
      refs.callSummaryInsertedRef.current = false
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [setStatus, setError])

  const chooseTranscription = useCallback(
    async (enabled: boolean) => {
      setTranscriptionEnabled(enabled)
      setIsMuted(false)
      setIsAiSpeaking(false)
      setTranscript([])
      setInterimUserTranscript('')
      setError(null)
      setStatus('choosing_voice')
      try {
        const result = await fetchRecommendedVoices()
        setVoices(result?.recommendedVoices || null)
        if (result?.recommendedVoices) {
          const locales = Object.keys(result.recommendedVoices)
          if (locales.length > 0) {
            const firstArr = result.recommendedVoices[locales[0]] || []
            const davisVoice = firstArr.find((v: any) => v.name === 'en-US-DavisNeural')
            if (davisVoice) {
              setSelectedVoice((prev) => prev ?? davisVoice.name)
            } else if (firstArr.length > 0) {
              setSelectedVoice((prev) => prev ?? firstArr[0].name)
            }
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
    },
    [setStatus, setTranscript, setError, setInterimUserTranscript]
  )

  const chooseVoice = useCallback(
    async (voiceName: string | null) => {
      setSelectedVoice(voiceName)
      setStatus('connecting')
      setIsMuted(false)
      setIsAiSpeaking(false)
      setError(null)

      const micStarted = await VAD.startMicCapture()

      const tId = window.setTimeout(() => {
        try {
          if (isPausedRef.current) {
            return
          }
          if (micStarted) {
            setStatus('listening')
            VAD.startListening()
            audio.startUtteranceRecording()
          } else {
            if (refs.permissionDeniedRef.current) {
              try {
                endCall()
              } catch (e) {
                calculateErrorMessage(e)
              }
              setStatus('ended')
              setError({
                type: 'permission_denied',
                message: 'Microphone permission was denied',
                retryable: false,
              })
              try {
                options?.onInsertChatEvent?.({
                  id: `mic-permission-denied-${Date.now()}`,
                  type: 'call_error',
                  title: 'Voice Call Unavailable',
                  message:
                    'Microphone permission was denied in your browser. Voice calls cannot be used without microphone access.',
                  sender: 'bot',
                  timestamp: new Date(),
                })
              } catch (e) {
                calculateErrorMessage(e)
              }
            } else {
              setStatus('error')
              setError({
                type: 'microphone_not_accessible',
                message: 'Microphone not accessible',
                retryable: true,
              })
            }
          }
        } finally {
          refs.timeoutIdsRef.current.delete(tId as any)
        }
      }, 400)
      refs.timeoutIdsRef.current.add(tId as any)
    },
    [setStatus, setError, transcriptionEnabled, VAD, audio]
  )

  const endCall = useCallback(() => {
    try {
      const prevStatus = refs.statusRef.current
      refs.abortRef.current?.abort()
      const {audio, vad, api} = bridgeRef.current
      if (audio) {
        audio.stopAudioPlayback()
        audio.stopFillerAudio()
        audio.stopProgressiveTranscript()
        audio.stopRecordingImmediate()
      }
      if (vad) {
        try {
          vad.stopListening()
        } catch (e) {
          calculateErrorMessage(e)
        }
        try {
          vad.stopMicCapture()
        } catch (e) {
          calculateErrorMessage(e)
        }
      }

      try {
        for (const id of Array.from(refs.timeoutIdsRef.current)) {
          try {
            clearTimeout(id as any)
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
      } finally {
        refs.timeoutIdsRef.current.clear()
      }

      refs.inFlightRef.current = false
      refs.responseRequestedRef.current = false
      refs.listeningBlockedRef.current = false
      refs.isAudioPlayingRef.current = false
      refs.interruptListeningActiveRef.current = false
      refs.ignoreRecognizerDuringAiRef.current = false

      api?.unblockListening('end', true)
      try {
        bridgeRef.current.audio?.revokeStoredPlaybackUrls?.()
      } catch (e) {
        calculateErrorMessage(e)
      }
      setStatus('ended')
      setIsMuted(false)
      setIsAiSpeaking(false)

      try {
        if (!refs.callSummaryInsertedRef.current) {
          const now = Date.now()
          const start = refs.maxListeningTimeRef.current || now
          const durationMs = Math.max(0, now - start)
          const disconnected =
            prevStatus === 'error' ||
            prevStatus === 'interrupted' ||
            (refs.abortRef.current && (refs.abortRef.current as any).signal?.aborted)

          refs.callSummaryInsertedRef.current = true
          try {
            options?.onInsertChatEvent?.({
              id: `call-summary-${Date.now()}`,
              type: 'call_summary',
              title: disconnected ? 'Voice Call Disconnected' : 'Voice Call Ended',
              durationMs,
              endedAt: now,
              sender: 'bot',
              timestamp: new Date(),
            })
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
    } catch (e) {
      setStatus('ended')
      setIsAiSpeaking(false)
      setIsMuted(false)
    }
  }, [setStatus, refs])

  const pauseCall = useCallback(() => {
    try {
      if (isPausedRef.current) return
      prevStatusRef.current = refs.statusRef.current || status
      prevAiSpeakingRef.current = refs.isAiSpeakingRef.current || isAiSpeaking
      setIsPaused(true)
      isPausedRef.current = true
      refs.walkthroughActiveRef.current = true

      try {
        const baseSnapshot = {
          transcript: transcript.map((t) => ({...t})),
          interimUserTranscript,
          selectedVoice,
          isMuted,
          lastCommittedUserTranscript: refs.lastCommittedUserTranscriptRef.current || null,
          awaitingResponse,
          isAiSpeaking,
          status: refs.statusRef.current || status,
          transcriptionEnabled,
        }

        let playback: WalkthroughSnapshot['playback'] | undefined = undefined
        try {
          const p = bridgeRef.current.audio?.pausePlayback?.()
          if (p) playback = p as any
          try {
            bridgeRef.current.audio?.progressiveManager?.pause?.()
          } catch (e) {
            calculateErrorMessage(e)
          }
        } catch (e) {
          calculateErrorMessage(e)
        }

        walkthroughSnapshotRef.current = {...baseSnapshot, playback}
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        setIsMuted(true)
        try {
          refs.mutedRef.current = true
        } catch (e) {
          calculateErrorMessage(e)
        }
        try {
          refs.listeningBlockedRef.current = true
        } catch (e) {
          calculateErrorMessage(e)
        }
      } catch (e) {
        calculateErrorMessage(e)
      }

      const {audio, vad} = bridgeRef.current
      try {
        vad?.stopListening()
      } catch (e) {
        calculateErrorMessage(e)
      }
      try {
        vad?.stopMicCapture()
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        audio?.pauseRecording?.()
        audio?.pauseUtteranceRecording?.()
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        for (const id of Array.from(refs.timeoutIdsRef.current)) {
          try {
            clearTimeout(id as any)
            pausedTimeoutsRef.current.add(id)
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
      } finally {
        refs.timeoutIdsRef.current.clear()
      }

      try {
        if (typeof document !== 'undefined') {
          const drawer = document.getElementById('kt_drawer_chat')
          const wasOpen = !!(
            drawer &&
            (drawer.classList.contains('show') ||
              drawer.getAttribute('data-kt-drawer-show') === 'true' ||
              (drawer.getBoundingClientRect && drawer.getBoundingClientRect().width > 0))
          )
          chatWasOpenBeforeWalkthroughRef.current = !!wasOpen
        }
      } catch (e) {
        calculateErrorMessage(e)
      }

      setStatus('walkthrough_paused')
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [status, isAiSpeaking, refs])

  const resumeCall = useCallback(async () => {
    try {
      if (!isPausedRef.current) return
      isPausedRef.current = false
      setIsPaused(false)
      refs.walkthroughActiveRef.current = false
      try {
        refs.listeningBlockedRef.current = false
      } catch (e) {
        calculateErrorMessage(e)
      }
      removeOverlay()

      const {audio, vad} = bridgeRef.current
      let restoredSnapshotStatus: VoiceCallStatus | null = null
      let restoredSnapshotSelectedVoice: string | null = null
      let savedPlayback: WalkthroughSnapshot['playback'] | undefined = undefined

      try {
        const snapshot = walkthroughSnapshotRef.current
        if (snapshot) {
          try {
            setTranscript(snapshot.transcript)
            setInterimUserTranscript(snapshot.interimUserTranscript)
            setSelectedVoice(snapshot.selectedVoice)
            setIsMuted(snapshot.isMuted)
            refs.lastCommittedUserTranscriptRef.current = snapshot.lastCommittedUserTranscript || ''
            setAwaitingResponse(snapshot.awaitingResponse)
            setIsAiSpeaking(snapshot.isAiSpeaking)
            setTranscriptionEnabled(snapshot.transcriptionEnabled ?? null)
            restoredSnapshotStatus = snapshot.status || null
            restoredSnapshotSelectedVoice = snapshot.selectedVoice || null
            savedPlayback = snapshot.playback
          } catch (e) {
            calculateErrorMessage(e)
          }
          walkthroughSnapshotRef.current = null

          try {
            if (chatWasOpenBeforeWalkthroughRef.current) {
              try {
                await restoreChatIfClosed()
              } catch (e) {
                calculateErrorMessage(e)
              }
            }
          } catch (e) {
            calculateErrorMessage(e)
          }

          try {
            bridgeRef.current.audio?.progressiveManager?.resume?.()
          } catch (e) {
            calculateErrorMessage(e)
          }

          try {
            const wasActiveCall =
              restoredSnapshotStatus === 'listening' ||
              restoredSnapshotStatus === 'connecting' ||
              restoredSnapshotStatus === 'responding' ||
              restoredSnapshotStatus === 'muted'

            if (wasActiveCall && restoredSnapshotSelectedVoice) {
              if (restoredSnapshotStatus === 'responding') {
                try {
                  await chooseVoice(restoredSnapshotSelectedVoice)
                  try {
                    onWalkthroughResume?.()
                  } catch (err) {
                    calculateErrorMessage(err)
                  }

                  if (savedPlayback) {
                    try {
                      await bridgeRef.current.audio?.resumePlayback?.(savedPlayback as any)
                      try {
                        bridgeRef.current.audio?.revokeStoredPlaybackUrls?.()
                      } catch (e) {
                        calculateErrorMessage(e)
                      }
                    } catch (e) {
                      calculateErrorMessage(e)
                    }
                  }
                  return
                } catch (e) {
                  calculateErrorMessage(e)
                }
              }
            }
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        const prev = prevStatusRef.current

        const desired = (restoredSnapshotStatus as VoiceCallStatus | null) || prev

        if (!desired) {
          const voiceToUse = restoredSnapshotSelectedVoice || selectedVoice
          if (voiceToUse) {
            try {
              await chooseVoice(voiceToUse)
              try {
                onWalkthroughResume?.()
              } catch (err) {
                calculateErrorMessage(err)
              }
              return
            } catch (e) {
              calculateErrorMessage(e)
            }
          }
        }

        if (desired === 'listening' || desired === 'connecting') {
          try {
            refs.mutedRef.current = false
            refs.listeningBlockedRef.current = false
          } catch (e) {
            calculateErrorMessage(e)
          }
          setStatus('connecting')

          let micStarted = await vad?.startMicCapture()
          if (!micStarted) {
            refs.permissionDeniedRef.current = false
            micStarted = await vad?.startMicCapture()
          }

          if (micStarted) {
            if (!isPausedRef.current) {
              try {
                vad?.startListening()
                audio?.startUtteranceRecording?.()
              } catch (e) {
                calculateErrorMessage(e)
              }
            }
            setStatus('listening')
            try {
              onWalkthroughResume?.()
            } catch (err) {
              calculateErrorMessage(err)
            }
          } else {
            if (refs.permissionDeniedRef.current) {
              try {
                endCall()
              } catch (e) {
                calculateErrorMessage(e)
              }
              setStatus('ended')
              setError({
                type: 'permission_denied',
                message: 'Microphone permission was denied',
                retryable: false,
              })
              try {
                options?.onInsertChatEvent?.({
                  id: `mic-permission-denied-${Date.now()}`,
                  type: 'call_error',
                  title: 'Voice Call Unavailable',
                  message:
                    'Microphone permission was denied in your browser. Voice calls cannot be used without microphone access.',
                  sender: 'bot',
                  timestamp: new Date(),
                })
              } catch (e) {
                calculateErrorMessage(e)
              }
            } else {
              try {
                endCall()
              } catch (e) {
                calculateErrorMessage(e)
              }
              setStatus('ended')
              setError({
                type: 'microphone_not_accessible',
                message: 'Microphone is not accessible. Please check your device settings.',
                retryable: false,
              })
              try {
                options?.onInsertChatEvent?.({
                  id: `mic-not-accessible-${Date.now()}`,
                  type: 'call_error',
                  title: 'Voice Call Unavailable',
                  message:
                    'Microphone is not accessible. Please check your device settings and try again.',
                  sender: 'bot',
                  timestamp: new Date(),
                })
              } catch (e) {
                calculateErrorMessage(e)
              }
            }
          }
        } else if (desired === 'responding') {
          setStatus('responding')
          setIsAiSpeaking(prevAiSpeakingRef.current)
          if (savedPlayback) {
            try {
              await audio?.resumePlayback?.(savedPlayback as any)
            } catch (e) {
              calculateErrorMessage(e)
            }
          }
          try {
            onWalkthroughResume?.()
          } catch (err) {
            calculateErrorMessage(err)
          }
        } else {
          if (desired) {
            setStatus(desired)
            try {
              onWalkthroughResume?.()
            } catch (err) {
              calculateErrorMessage(err)
            }
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        pausedTimeoutsRef.current.clear()
      } catch (e) {
        calculateErrorMessage(e)
      }
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [setStatus, setError])

  const returnToIdle = useCallback(() => {
    endCall()
    try {
      bridgeRef.current.audio?.revokeStoredPlaybackUrls?.()
    } catch (e) {
      calculateErrorMessage(e)
    }
    setStatus('idle')
    setTranscriptionEnabled(null)
    setTranscript([])
    setInterimUserTranscript('')
    setError(null)
    try {
      refs.callSummaryInsertedRef.current = false
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [endCall, setStatus, setTranscript, setInterimUserTranscript, setError])

  useEffect(() => {
    if (status !== 'listening' || isMuted || awaitingResponse || isAiSpeaking) return
    const interval = setInterval(() => {
      const now = Date.now()
      const timeSinceLastAudio = now - refs.lastAudioActivityRef.current
      const hasRealSpeech = refs.realSpeechDetectedRef.current
      const hasInterimText = (refs.interimUserTranscriptRef.current || '').trim().length > 5
      // 2.8s, not 2s: Urdu speakers pause mid-question ("Ahmed ki... pichle mahine ki attendance"),
      // and a 2s cut sent half a question, which was then answered as asked.
      const silenceTime = hasRealSpeech || hasInterimText ? 2800 : 4000

      const hasVoiceActivity = refs.hasVoiceActivityRef.current || hasRealSpeech || hasInterimText
      const listeningBlocked = refs.listeningBlockedRef.current
      const responseRequested = refs.responseRequestedRef.current
      const speechStartAge = now - refs.speechStartTimeRef.current

      if (
        hasVoiceActivity &&
        !responseRequested &&
        !listeningBlocked &&
        (hasRealSpeech || hasInterimText) &&
        timeSinceLastAudio > silenceTime &&
        speechStartAge > 1000
      ) {
        apiRef.current?.handleTransitionToResponding(audioRef.current?.refs)
      }
    }, 200)
    return () => clearInterval(interval)
  }, [status, isMuted, awaitingResponse, isAiSpeaking])

  // The two-second watchdog that used to sit here restarted a Web Speech
  // session that had quietly ended on its own. There is no browser recogniser
  // any more, and mic capture runs for the whole call, so there is nothing to
  // nurse back to life.

  const retryCall = useCallback(() => {
    setError(null)
    setStatus('choosing_transcription')
  }, [setError, setStatus])

  const isCallSupported = useMemo(() => {
    return (
      typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia
    )
  }, [])

  return {
    status,
    transcriptionEnabled,
    isMuted,
    isAiSpeaking,
    error,
    transcript,
    interimUserTranscript,
    micLevel,
    awaitingResponse,
    voices,
    selectedVoice,
    isCallSupported,
    startCallFlow,
    chooseTranscription,
    chooseVoice,
    endCall,
    returnToIdle,
    toggleMute: () => setIsMuted((prev) => !prev),
    setSelectedVoice,
    retryCall,
    pauseCall,
    resumeCall,
    isPaused,
  }
}
